'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import SafeImage from '@/hotdesk-ui/components/shared/SafeImage';
import { useLoadableImages } from '@/hotdesk-ui/hooks/useLoadableImages';
import { ImageSize, sizedImageUrl } from '@/hotdesk-ui/helpers/image-url';
import styles from './ImageGallery.module.scss';
export interface ImageGalleryProps {
  images: Record<string, string>;
  title: string;
}
const GRID_AREAS = ['a', 'b', 'c', 'd', 'e'] as const;
type LayoutVariant = 'single' | 'double' | 'triple' | 'quad' | 'full';
const pickVariant = (count: number): LayoutVariant => {
  if (count <= 1) return 'single';
  if (count === 2) return 'double';
  if (count === 3) return 'triple';
  if (count === 4) return 'quad';
  return 'full';
};
const VISIBLE_SLOTS: Record<LayoutVariant, number> = {
  single: 1,
  double: 2,
  triple: 3,
  quad: 4,
  full: 5
};
const VARIANT_CLASS: Record<LayoutVariant, string> = {
  single: styles.gridSingle,
  double: styles.gridDouble,
  triple: styles.gridTriple,
  quad: styles.gridQuad,
  full: styles.gridFull
};

// Photo sizes per slot, tied to the CSS widths: desktop main slot 608px,
// desktop thumbnails 296px, mobile main photo ~full width, mobile
// thumbnails ~half width (sharp up to 3x screens).
const SIZES = {
  desktopMain: 'lg',
  desktopThumb: 'md',
  mobileMain: 'md',
  mobileThumb: 'sm',
  lightbox: 'lg',
  probe: 'sm'
} as const satisfies Record<string, ImageSize>;

/** Matches the stylesheet's switch between the two grids. */
const MOBILE_MEDIA = '(max-width: 767px)';
const DESKTOP_MEDIA = '(min-width: 768px)';
/**
 * Both grids are in the page and CSS hides one, but a high-priority photo
 * downloads even when hidden. The hidden grid's main photo gets this inline
 * 1px image instead, so each device fetches only its own main photo.
 */
const BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const ImageGallery: FC<ImageGalleryProps> = ({
  images,
  title
}) => {
  const t = useTranslations('workspace-details');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // The main photo is usually the page's LCP, so photos render straight
  // away (sizes come from code, not Remote Config) and the broken-photo
  // probe waits until the page has loaded, using the small size.
  const [probeReady, setProbeReady] = useState(false);
  useEffect(() => {
    const start = () => setProbeReady(true);
    if (document.readyState === 'complete') start();else window.addEventListener('load', start, {
      once: true
    });
    return () => window.removeEventListener('load', start);
  }, []);

  // Broken photos are also dropped as they fail to render (onAllFailed);
  // the probe catches the ones not on screen (lightbox, "+N photos").
  const {
    images: imagesArray,
    isFallback,
    markBroken
  } = useLoadableImages(Object.values(images || {}), {
    probe: probeReady,
    toProbeUrl: src => sizedImageUrl(src, SIZES.probe)
  });
  const count = imagesArray.length;
  const variant = pickVariant(count);
  const visibleCount = Math.min(count, VISIBLE_SLOTS[variant]);
  const extraCount = Math.max(0, count - visibleCount);

  // The default photo has no CDN size variants, so it is used as-is.
  const getImageSrc = (index: number, size: ImageSize) => isFallback ? imagesArray[index] : sizedImageUrl(imagesArray[index], size);
  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };
  const goToSlide = (index: number) => {
    setLightboxIndex(Math.max(0, Math.min(index, imagesArray.length - 1)));
  };

  // Keep the lightbox in range when a broken image is removed.
  useEffect(() => {
    if (lightboxIndex > count - 1) setLightboxIndex(Math.max(0, count - 1));
  }, [lightboxIndex, count]);
  useEffect(() => {
    if (!lightboxOpen) return;
    closeButtonRef.current?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToSlide(lightboxIndex - 1);
      if (e.key === 'ArrowRight') goToSlide(lightboxIndex + 1);
      if (e.key === 'Tab') {
        e.preventDefault();
        closeButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxIndex]);
  const visibleImages = imagesArray.slice(0, visibleCount);
  const mobileVisibleCount = count >= 3 ? 3 : count;
  const mobileVariantClass = count === 1 ? styles.mobileSingle : count === 2 ? styles.mobileDouble : styles.mobileTriple;
  const mobileExtraCount = Math.max(0, count - mobileVisibleCount);
  return <>
            <div className={styles.gallery}>
                {/* Desktop: adaptive layout */}
                <div className={`${styles.desktopGrid} ${VARIANT_CLASS[variant]}`}>
                    {visibleImages.map((_, i) => {
          const isLastVisible = i === visibleCount - 1;
          const showOverlay = isLastVisible && extraCount > 0;
          return <button key={i} type="button" className={styles.slot} style={{
            gridArea: GRID_AREAS[i]
          }} onClick={() => openLightbox(i)}>
                                <SafeImage src={getImageSrc(i, i === 0 ? SIZES.desktopMain : SIZES.desktopThumb)}
            // Hidden on phones: never fetch it there.
            mobileSrc={i === 0 ? BLANK_IMAGE : undefined} mobileMedia={MOBILE_MEDIA} alt={`${title} - ${i + 1}`} fill priority={i === 0} onAllFailed={() => markBroken(imagesArray[i])} sizes={i === 0 ? '(max-width: 767px) 100vw, 608px' : '(max-width: 767px) 140px, 296px'} style={{
              objectFit: 'cover'
            }} />
                                {showOverlay && <span className={styles.moreOverlay}>
                                        + {extraCount} {t('morePhotos')}
                                    </span>}
                            </button>;
        })}
                </div>

                {/* Mobile: adaptive layout */}
                <div className={`${styles.mobileGrid} ${mobileVariantClass}`}>
                    {imagesArray.slice(0, mobileVisibleCount).map((_, i) => {
          const isLastVisible = i === mobileVisibleCount - 1;
          const showOverlay = isLastVisible && mobileExtraCount > 0;
          return <button key={i} type="button" className={styles.slot} style={{
            gridArea: GRID_AREAS[i]
          }} onClick={() => openLightbox(i)}>
                                <SafeImage src={getImageSrc(i, i === 0 ? SIZES.mobileMain : SIZES.mobileThumb)}
            // Hidden on desktop: never fetch it there.
            mobileSrc={i === 0 ? BLANK_IMAGE : undefined} mobileMedia={DESKTOP_MEDIA} alt={`${title} - ${i + 1}`} fill onAllFailed={() => markBroken(imagesArray[i])} sizes="(max-width: 767px) 50vw, 140px" style={{
              objectFit: 'cover'
            }} {...i === 0 ? {
              priority: true
            } : {
              loading: 'lazy' as const
            }} />
                                {showOverlay && <span className={styles.moreOverlay}>
                                        + {mobileExtraCount} {t('morePhotos')}
                                    </span>}
                            </button>;
        })}
                </div>
            </div>

            {/* Lightbox Modal */}
            {lightboxOpen && <div className={styles.lightbox} role="dialog" aria-modal="true" aria-label="Image gallery">
                    <div className={styles.lightboxBackdrop} onClick={closeLightbox} />
                    <button ref={closeButtonRef} type="button" className={styles.lightboxClose} onClick={closeLightbox} aria-label="Close gallery">
                        <X size={24} />
                    </button>
                    <div className={styles.lightboxContent}>
                        {lightboxIndex > 0 && <button type="button" className={`${styles.lightboxArrow} ${styles.lightboxPrev}`} onClick={() => goToSlide(lightboxIndex - 1)} aria-label="Previous image">
                                <ChevronLeft size={28} />
                            </button>}
                        <div className={styles.lightboxImageWrap}>
                            <SafeImage src={getImageSrc(lightboxIndex, SIZES.lightbox)} alt={`${title} - ${lightboxIndex + 1}`} fill onAllFailed={() => markBroken(imagesArray[lightboxIndex])} sizes="90vw" style={{
            objectFit: 'contain'
          }} />
                        </div>
                        {lightboxIndex < imagesArray.length - 1 && <button type="button" className={`${styles.lightboxArrow} ${styles.lightboxNext}`} onClick={() => goToSlide(lightboxIndex + 1)} aria-label="Next image">
                                <ChevronRight size={28} />
                            </button>}
                    </div>
                    <div className={styles.lightboxCounter}>
                        {lightboxIndex + 1} / {imagesArray.length}
                    </div>
                </div>}
        </>;
};
export default ImageGallery;